<template>
  <!-- Updated v-if to correctly check for rating and reviews (reviews can be 0 or string/number) -->
  <div v-if="rating !== null && reviews !== null && rating !== undefined && reviews !== undefined" class="bg-gradient-to-r from-[#006D5E] to-[#00574B] text-white shadow-md relative z-10"> <!-- Brand teal, not Trustpilot's green: their compliance team objects to look-alike rating imagery -->
    <div class="container mx-auto px-4 py-2">
      <div class="flex flex-wrap justify-center items-center gap-x-4 gap-y-1 text-sm">

        <!-- Rating Section - Replicating original star logic -->
        <div class="flex items-center gap-2">
          <!-- Dynamic Stars -->
          <div class="flex gap-px"> <!-- Small gap between stars -->
            <div v-for="i in 5" :key="i" class="w-4 h-4 relative"> <!-- Star container -->
              <!-- Background Star (Dimmed) -->
              <svg class="absolute inset-0 w-full h-full text-white/30" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 0l3.09 6.3 6.91.99-5 4.87 1.18 6.88L10 15.4l-6.18 3.64 1.18-6.88-5-4.87 6.91-.99L10 0z"/>
              </svg>
              <!-- Foreground Star (Filled based on rating) -->
              <svg class="absolute inset-0 w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20" :style="getStarStyle(i)">
                <path d="M10 0l3.09 6.3 6.91.99-5 4.87 1.18 6.88L10 15.4l-6.18 3.64 1.18-6.88-5-4.87 6.91-.99L10 0z"/>
              </svg>
            </div>
          </div>
          <!-- Rating Text -->
          <div class="text-xs font-medium text-shadow-sm"> <!-- Adjusted text size/weight -->
             {{ rating }} / 5 • {{ formatNumber(reviews) }} reviews
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';

// Access shared Trustpilot data from 'trustpilotStats' prop
const page = usePage();
// Ensure rating and reviews are treated as numbers for calculation
const rating = computed(() => {
    const val = page.props.trustpilotStats?.rating;
    // Convert string rating like "4.7" to number
    return (typeof val === 'string' || typeof val === 'number') ? parseFloat(val) : null;
});
const reviews = computed(() => {
    const val = page.props.trustpilotStats?.reviews;
     // Convert string reviews like "331" to number
    return (typeof val === 'string' || typeof val === 'number') ? parseInt(val, 10) : null;
});


// Formatting helper from the old component
const formatNumber = (num) => {
    // Check if num is a valid number before formatting
    if (typeof num !== 'number' || isNaN(num)) return 'N/A';
    return new Intl.NumberFormat().format(num);
};

// Star calculation logic - Replicated from old component
const getStarStyle = (position) => {
    const numericRating = rating.value; // Already a number or null from computed

    // Handle cases where rating is null or not a valid number
    if (numericRating === null || isNaN(numericRating)) {
        return { clipPath: 'inset(0 100% 0 0)' }; // Empty star if rating is invalid
    }

    if (position <= Math.floor(numericRating)) {
        return { clipPath: 'inset(0 0% 0 0)' }; // Full star
    }

    const decimal = numericRating - Math.floor(numericRating);
    if (position === Math.ceil(numericRating)) {
        const percentage = Math.round(decimal * 100);
        // Ensure percentage is within 0-100 range
        const clipPercentage = Math.max(0, Math.min(100, 100 - percentage));
        return { clipPath: `inset(0 ${clipPercentage}% 0 0)` }; // Partial star
    }

    return { clipPath: 'inset(0 100% 0 0)' }; // Empty star
}
</script>

<style scoped>
/* Add Tailwind's text-shadow utility if needed, or define custom */
.text-shadow-sm {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
</style>
